import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Lightbulb, Search, TrendingUp } from "lucide-react";
import { format } from "date-fns";

interface BlogRequest {
  id: string;
  requester_name: string | null;
  requester_email: string;
  topic: string;
  description: string;
  category_preference: string | null;
  urgency: string;
  status: string;
  votes: number;
  admin_notes: string | null;
  created_at: string;
}

const BlogRequests = () => {
  const { toast } = useToast();
  const [requests, setRequests] = useState<BlogRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<BlogRequest | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from("blog_post_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error: any) {
      console.error("Error fetching requests:", error);
      toast({
        title: "Error",
        description: "Failed to load blog requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;

    try {
      const { error } = await supabase
        .from("blog_post_requests")
        .update({
          admin_notes: adminNotes,
          status: selectedStatus,
        })
        .eq("id", selectedRequest.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Blog request updated successfully",
      });

      setIsDialogOpen(false);
      fetchRequests();
    } catch (error: any) {
      console.error("Error updating request:", error);
      toast({
        title: "Error",
        description: "Failed to update request",
        variant: "destructive",
      });
    }
  };

  const openDialog = (request: BlogRequest) => {
    setSelectedRequest(request);
    setAdminNotes(request.admin_notes || "");
    setSelectedStatus(request.status);
    setIsDialogOpen(true);
  };

  const filteredRequests = requests.filter((request) => {
    const matchesSearch =
      request.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.requester_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || request.status === statusFilter;
    const matchesUrgency = urgencyFilter === "all" || request.urgency === urgencyFilter;
    return matchesSearch && matchesStatus && matchesUrgency;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "submitted": return "bg-blue-500";
      case "under_review": return "bg-yellow-500";
      case "approved": return "bg-green-500";
      case "published": return "bg-purple-500";
      case "declined": return "bg-gray-500";
      default: return "bg-gray-500";
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "high": return "bg-red-500";
      case "medium": return "bg-yellow-500";
      case "low": return "bg-green-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Blog Post Requests</h1>
          <p className="text-muted-foreground mt-2">
            Review and manage user-requested blog topics
          </p>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by topic or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
            <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by urgency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Urgencies</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredRequests.length === 0 ? (
          <Card className="p-12 text-center">
            <Lightbulb className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No blog requests found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => (
              <Card
                key={request.id}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => openDialog(request)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getStatusColor(request.status)}>
                        {request.status.replace("_", " ")}
                      </Badge>
                      <Badge className={getUrgencyColor(request.urgency)}>
                        {request.urgency}
                      </Badge>
                      {request.votes > 0 && (
                        <Badge variant="outline">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {request.votes}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {request.topic}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      From: {request.requester_name || "Anonymous"} ({request.requester_email})
                    </p>
                    {request.category_preference && (
                      <p className="text-xs text-muted-foreground mb-2">
                        Suggested Category: {request.category_preference}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(request.created_at), "PPpp")}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Blog Request Details</DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div>
                  <Label>Topic</Label>
                  <p className="text-lg font-semibold">{selectedRequest.topic}</p>
                </div>
                <div>
                  <Label>Why this topic is important</Label>
                  <p className="text-sm whitespace-pre-wrap bg-muted p-3 rounded">
                    {selectedRequest.description}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>From</Label>
                    <p className="text-sm">
                      {selectedRequest.requester_name || "Anonymous"} ({selectedRequest.requester_email})
                    </p>
                  </div>
                  <div>
                    <Label>Urgency</Label>
                    <p className="text-sm capitalize">{selectedRequest.urgency}</p>
                  </div>
                </div>
                {selectedRequest.category_preference && (
                  <div>
                    <Label>Suggested Category</Label>
                    <p className="text-sm">{selectedRequest.category_preference}</p>
                  </div>
                )}
                <div>
                  <Label>Votes</Label>
                  <p className="text-sm">{selectedRequest.votes}</p>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="under_review">Under Review</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="admin_notes">Admin Notes</Label>
                  <Textarea
                    id="admin_notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this request..."
                    rows={4}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateRequest}>
                    Update Request
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default BlogRequests;
